import React, { useState } from "react";

import Typography from "@mui/material/Typography";

import PlanDetail from "@app/analytics/account/Billing/components/PlanDetail";
import {
  getAnnualSavings,
  getTruncatedPlanLabel,
  isPaidPlan,
  isZoomPlan,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import type { BillingCadence } from "@app/common/types";
import { planIsUnknown } from "@app/common/utils/billingUtils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Chip from "@rui/Chip";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import { Span } from "@rui/styles";
import Switch from "@rui/Switch";

const BillingPlanCompare = () => {
  usePageTitle("Billing - Compare Plans");

  const {
    currentPlan,
    isEligibleForLegacyPricing,
    isTrial,
    scheduledPlan,
    plansInitialLoading,
  } = useBillingContext();

  const [selectedCadence, setSelectedCadence] = useState<BillingCadence | null>(
    null
  );

  if (plansInitialLoading) {
    return (
      <Stack variant="center" flex={1}>
        <LoadSpinner />
      </Stack>
    );
  }

  // if the switch is dirty (selected cadence is not null), use that. otherwise use the
  // user's current cadence, defaulting to annual if there is not one
  const cadence = selectedCadence ?? currentPlan.billingCadence ?? "annual";

  const handleToggleCadence = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setSelectedCadence(checked ? "annual" : "monthly");
  };

  const detailProps = {
    cadence,
    currentPlan: isTrial ? undefined : currentPlan,
    scheduledPlan,
    useLegacyPrices: isEligibleForLegacyPricing,
  };

  const annualSavings = getAnnualSavings(isEligibleForLegacyPricing);
  const showAnnualPricing = cadence === "annual";
  const creditMayApply = !isTrial && isPaidPlan(currentPlan.type);
  const highlightedFreePlan = isZoomPlan(currentPlan.type)
    ? "zoom_bundle_trial"
    : "free";

  return (
    <Stack spacing={4} sx={{ padding: "40px 60px" }} flex={1}>
      <Stack variant="center" spacing={2}>
        {!planIsUnknown(currentPlan.type) && (
          <Typography sx={{ fontWeight: 500, fontSize: "2.25rem" }}>
            You're on the{" "}
            <Span sx={{ color: "shades.primary.500" }}>
              {getTruncatedPlanLabel(currentPlan.type)}
            </Span>{" "}
            {isTrial ? "trial" : "plan"}
          </Typography>
        )}
        <Row variant="center" spacing={1}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              color: showAnnualPricing
                ? "shades.neutral.500"
                : "shades.primary.500",
            }}
          >
            Monthly
          </Typography>
          <Switch
            data-test-id="billing-compare-plans-cadence-toggle"
            checked={showAnnualPricing}
            onChange={handleToggleCadence}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              color: showAnnualPricing
                ? "shades.primary.500"
                : "shades.neutral.500",
            }}
          >
            Annually
          </Typography>
          <Chip color="chart.orange.dark" bgcolor="shades.orange.50">
            Save {annualSavings}%
          </Chip>
        </Row>
      </Stack>
      <Row spacing={1.5} flexWrap="wrap" variant="center">
        <Row spacing={1.5} alignSelf="flex-end">
          <PlanDetail {...detailProps} plan={highlightedFreePlan} />
          <PlanDetail {...detailProps} plan="pro" />
        </Row>
        <Row spacing={1.5} alignSelf="flex-end">
          <PlanDetail {...detailProps} plan="enterprise" />
          <PlanDetail {...detailProps} plan="enterprise_plus" />
        </Row>
      </Row>
      {creditMayApply && (
        <Row variant="center" paddingTop={1}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            If upgrading, a credit will be applied during checkout.
          </Typography>
        </Row>
      )}
    </Stack>
  );
};

export default BillingPlanCompare;
